// The only place an in-app URL is constructed. Every screen, every nav row and
// every redirect goes through here, so an org-scoping change is one edit.
//
// PRD 18 US-011. Screens: import `hrefs(orgSlug)` or `adminHrefs()` — do not
// template a `/dashboard/${slug}/...` or `/admin/...` string by hand anywhere.
// `crossCutting.test.ts` fails the build on both.

export type NavDestination = {
  /** Stable key; also the nav row's `data-nav` attribute. */
  key: string;
  label: string;
  href: string;
};

/**
 * Everything the chassis needs to draw itself for one audience.
 *
 * This type exists because the operator console is the second caller of
 * `AppShell`, and it needs a different nav and a different identity chip while
 * being the same chassis in every other respect. A second shell component would
 * have duplicated the `data-chassis="paper"` scoping that every rule in
 * `ui.css` hangs off — the trap `PRDs/18-figma-surfaces/ORCHESTRATOR_NOTES.md`
 * documents three separate ways to fall into.
 */
export type ShellNav = {
  /** Sidebar account row and topbar account-menu label. */
  identity: string;
  /** The sidebar's single section heading. */
  section: string;
  items: NavDestination[];
  /** Rendered below the spacer, at the foot. Absent for the operator nav. */
  pinned?: NavDestination;
};

export function hrefs(orgSlug: string) {
  const base = `/dashboard/${orgSlug}`;
  return {
    overview: base,
    documents: `${base}/documents`,
    vocabulary: `${base}/vocabulary`,
    analytics: `${base}/analytics`,
    // The three axes as destinations. `activeNavKey`'s longest-prefix rule
    // means the sidebar's single Analytics row lights for all of them, which
    // is why none of these is a `navDestinations` entry of its own.
    analyticsUsage: `${base}/analytics/usage`,
    analyticsEngagement: `${base}/analytics/engagement`,
    analyticsCoupling: `${base}/analytics/coupling`,
    term: (termId: string) => `${base}/terms/${termId}`,
    install: `${base}/install`,
    billing: `${base}/billing`,
    billingSuccess: `${base}/billing/success`,
    billingCancelled: `${base}/billing/cancelled`,
    settings: `${base}/settings`,
    support: `${base}/support`,
    supportSent: `${base}/support/sent`,
  };
}

/** The seven sidebar destinations, in drawn order. Support is pinned separately. */
export function navDestinations(orgSlug: string): NavDestination[] {
  const to = hrefs(orgSlug);
  return [
    { key: "overview", label: "Overview", href: to.overview },
    { key: "documents", label: "Documents", href: to.documents },
    { key: "vocabulary", label: "Vocabulary health", href: to.vocabulary },
    { key: "analytics", label: "Analytics", href: to.analytics },
    { key: "install", label: "Install", href: to.install },
    { key: "billing", label: "Plan & billing", href: to.billing },
    { key: "settings", label: "Settings", href: to.settings },
  ];
}

export function supportDestination(orgSlug: string): NavDestination {
  return { key: "support", label: "Support", href: hrefs(orgSlug).support };
}

/** The tenant workspace chassis — today's behaviour, named. */
export function orgNav(orgSlug: string): ShellNav {
  return {
    identity: orgSlug,
    section: "Workspace",
    items: navDestinations(orgSlug),
    pinned: supportDestination(orgSlug),
  };
}

// ---- operator console -------------------------------------------------------
//
// Cross-tenant, so nothing here is org-scoped. Three leaves, no more: the
// global spend readout is a stat row on the tenants screen rather than a route
// of its own, and impersonation is a POST, not a page.

export function adminHrefs() {
  return {
    home: "/admin",
    tenant: (orgSlug: string) => `/admin/orgs/${orgSlug}`,
    support: "/admin/support",
  };
}

export function adminNav(email: string): ShellNav {
  const to = adminHrefs();
  return {
    // The operator's email, not an org: while impersonating, the sidebar of the
    // TENANT shell shows the tenant, and this one must never be mistaken for it.
    identity: email,
    section: "Operator",
    items: [
      { key: "admin-tenants", label: "Tenants", href: to.home },
      { key: "admin-support", label: "Support inbox", href: to.support },
    ],
  };
}

/**
 * Longest matching prefix wins, on segment boundaries only. `/dashboard/acme`
 * is a prefix of every other destination, so without the longest-match rule the
 * whole sidebar would light up; without the prefix rule `/dashboard/acme/terms/x`
 * would light up nothing at all.
 *
 * Takes the nav rather than an org slug: it always built its candidate list
 * from one, and passing the list in removes a branch instead of adding one.
 */
export function activeNavKey(pathname: string, nav: ShellNav): string | null {
  const candidates = nav.pinned ? [...nav.items, nav.pinned] : nav.items;
  let best: NavDestination | null = null;
  for (const item of candidates) {
    if (pathname !== item.href && !pathname.startsWith(`${item.href}/`)) continue;
    if (!best || item.href.length > best.href.length) best = item;
  }
  return best ? best.key : null;
}
